import { useTranslation } from "react-i18next";
import PublicHeader from "@/components/layout/PublicHeader";
import PublicFooter from "@/components/layout/PublicFooter";
import { Button } from "@/components/ui/button";
import { Download, ExternalLink, FileText, BookOpen, Users, Database } from "lucide-react";
import { motion } from "framer-motion";
import "@/i18n/fase5";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function Documentacion() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const roles = [
    { role: "ADMIN", perms: ["✓ Todos os módulos", "✓ Xestión usuarios / API keys", "✓ Auditoría"] },
    { role: "SUPERVISOR", perms: ["✓ Módulos operativos", "✓ CRUD pesaxes", "— Sen auditoría"] },
    { role: "MANCOMUNIDADE", perms: ["✓ Módulos institucionais", "✓ Informes 'Aprobado'", "— Sen operativa CRUD"] },
    { role: "PUBLIC", perms: ["✓ Landing pública", "✓ Datos Abertos", "— Sen /app/*"] },
  ];

  const modules = [
    { key: "geoportal", icon: Database, name: "Xeoportal / Geoportal", body: t("project.cards.geoportal.body", { defaultValue: "" }) },
    { key: "esg", icon: FileText, name: "ESG", body: "Marco Ambiental, Social e de Gobernanza (GRI · Axenda Urbana)." },
    { key: "pegada", icon: BookOpen, name: "Pegada de Carbono", body: "Motor MRV ISO 14064 con factores oficiais MITECO/IPCC." },
    { key: "incidencias", icon: Users, name: "Incidencias IA", body: "Análise de deteccións automatizadas da rede Jetson (RGPD-safe)." },
    { key: "informe", icon: FileText, name: "Informe PRTR", body: "Xerador de documento oficial NextGenerationEU con SHA-256." },
    { key: "datos-abertos", icon: Database, name: "Datos Abertos", body: "API pública CC BY 4.0 con rate limiting." },
  ];

  return (
    <div className="min-h-screen bg-salnes-ivory" data-testid="page-documentacion">
      <PublicHeader />
      <section className="relative pt-32 pb-16 border-b hairline">
        <div className="absolute inset-0 opacity-[0.08]" style={{ backgroundImage: "url(/brand/hero-salnes-alt2.jpg)", backgroundSize: "cover", backgroundPosition: "center" }} />
        <div className="relative mx-auto max-w-[1100px] px-6 lg:px-10">
          <div className="text-[11px] uppercase tracking-[0.28em] text-salnes-emerald mb-3">{t("common.institutional")}</div>
          <motion.h1 initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="font-serif text-5xl lg:text-6xl text-salnes-ink">
            {t("fase5.doc_title")}
          </motion.h1>
          <p className="mt-4 text-lg text-salnes-ink/80 max-w-2xl">{t("fase5.doc_subtitle")}</p>
        </div>
      </section>

      <section className="py-16">
        <div className="mx-auto max-w-[1100px] px-6 lg:px-10 grid lg:grid-cols-[240px_1fr] gap-10">
          {/* Sidebar TOC */}
          <aside className="lg:sticky lg:top-24 self-start">
            <div className="text-[11px] uppercase tracking-[0.22em] text-salnes-stone mb-3">Índice</div>
            <nav className="space-y-2 text-sm">
              <a href="#about" className="block hover:text-salnes-atlantic">{t("fase5.doc_about")}</a>
              <a href="#modules" className="block hover:text-salnes-atlantic">{t("fase5.doc_modules")}</a>
              <a href="#roles" className="block hover:text-salnes-atlantic">{t("fase5.doc_roles")}</a>
              <a href="#downloads" className="block hover:text-salnes-atlantic">{t("fase5.doc_downloads")}</a>
            </nav>
          </aside>

          <div className="space-y-12">
            <section id="about">
              <h2 className="font-serif text-3xl text-salnes-ink mb-3">{t("fase5.doc_about")}</h2>
              <p className="text-salnes-ink/85 leading-relaxed">{t("fase5.doc_about_body")}</p>
            </section>

            <section id="modules">
              <h2 className="font-serif text-3xl text-salnes-ink mb-4">{t("fase5.doc_modules")}</h2>
              <div className="grid md:grid-cols-2 gap-4">
                {modules.map((m) => (
                  <div key={m.key} className="rounded-2xl border hairline bg-white p-5" data-testid={`doc-module-${m.key}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <m.icon size={16} className="text-salnes-atlantic" />
                      <div className="font-serif text-lg text-salnes-ink">{m.name}</div>
                    </div>
                    <div className="text-sm text-salnes-stone leading-relaxed">{m.body}</div>
                  </div>
                ))}
              </div>
            </section>

            <section id="roles">
              <h2 className="font-serif text-3xl text-salnes-ink mb-4">{t("fase5.doc_roles")}</h2>
              <div className="rounded-2xl border hairline bg-white overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-salnes-sand/40 text-salnes-stone text-[10px] uppercase tracking-[0.18em]">
                    <tr><th className="text-left px-4 py-3">Rol</th><th className="text-left px-4 py-3">Permisos</th></tr>
                  </thead>
                  <tbody className="divide-y hairline">
                    {roles.map((r) => (
                      <tr key={r.role}>
                        <td className="px-4 py-3 font-mono text-xs text-salnes-atlantic font-medium">{r.role}</td>
                        <td className="px-4 py-3 text-salnes-ink text-[13px]">{r.perms.join(" · ")}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            <section id="downloads">
              <h2 className="font-serif text-3xl text-salnes-ink mb-4">{t("fase5.doc_downloads")}</h2>
              <div className="grid md:grid-cols-2 gap-4">
                <a href={`${BACKEND}/api/docs/user-manual?lang=${lang}`} target="_blank" rel="noreferrer" data-testid="doc-download-user-manual">
                  <div className="rounded-2xl border hairline bg-white p-5 hover:shadow-md transition-all">
                    <div className="flex items-center gap-3">
                      <Download size={20} className="text-salnes-atlantic" />
                      <div>
                        <div className="font-serif text-lg text-salnes-ink">{t("fase5.doc_download_user")}</div>
                        <div className="text-xs text-salnes-stone">PDF · {lang.toUpperCase()}</div>
                      </div>
                    </div>
                  </div>
                </a>
                <a href={`${BACKEND}/api/docs/tech-manual?lang=${lang}`} target="_blank" rel="noreferrer" data-testid="doc-download-tech-manual">
                  <div className="rounded-2xl border hairline bg-white p-5 hover:shadow-md transition-all">
                    <div className="flex items-center gap-3">
                      <Download size={20} className="text-salnes-atlantic" />
                      <div>
                        <div className="font-serif text-lg text-salnes-ink">{t("fase5.doc_download_tech")}</div>
                        <div className="text-xs text-salnes-stone">PDF · {lang.toUpperCase()}</div>
                      </div>
                    </div>
                  </div>
                </a>
                <a href={`${BACKEND}/api/openapi.json`} target="_blank" rel="noreferrer" data-testid="doc-download-openapi">
                  <div className="rounded-2xl border hairline bg-white p-5 hover:shadow-md transition-all">
                    <div className="flex items-center gap-3">
                      <ExternalLink size={20} className="text-salnes-atlantic" />
                      <div>
                        <div className="font-serif text-lg text-salnes-ink">{t("fase5.doc_download_openapi")}</div>
                        <div className="text-xs text-salnes-stone">JSON schema</div>
                      </div>
                    </div>
                  </div>
                </a>
                <a href={`${BACKEND}/api/docs`} target="_blank" rel="noreferrer" data-testid="doc-download-swagger">
                  <div className="rounded-2xl border hairline bg-white p-5 hover:shadow-md transition-all">
                    <div className="flex items-center gap-3">
                      <ExternalLink size={20} className="text-salnes-atlantic" />
                      <div>
                        <div className="font-serif text-lg text-salnes-ink">{t("fase5.doc_download_swagger")}</div>
                        <div className="text-xs text-salnes-stone">Interactive API explorer</div>
                      </div>
                    </div>
                  </div>
                </a>
              </div>
            </section>
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
